
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import ImageUpload from '@/components/shared/ImageUpload'

interface ContentImage {
  id: string
  section_name: string
  image_url: string
  alt_text: string | null
  display_order: number
  is_active: boolean
}

interface ContentImageFormProps {
  contentImage?: ContentImage | null
  onSave: () => void
  onCancel: () => void
}

const SECTION_OPTIONS = [
  { value: 'hero', label: 'Hero Section' },
  { value: 'services', label: 'Services Section' },
  { value: 'projects', label: 'Projects Section' },
  { value: 'about', label: 'About Section' },
  { value: 'clients', label: 'Clients Section' },
  { value: 'contact', label: 'Contact Section' },
]

const ContentImageForm = ({ contentImage, onSave, onCancel }: ContentImageFormProps) => {
  const [loading, setLoading] = useState(false)
  const [imageUrl, setImageUrl] = useState(contentImage?.image_url || '')
  const { toast } = useToast()
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      section_name: contentImage?.section_name || '',
      alt_text: contentImage?.alt_text || '',
      display_order: contentImage?.display_order || 0,
      is_active: contentImage?.is_active ?? true,
    }
  })

  const watchedSectionName = watch('section_name')
  const watchedIsActive = watch('is_active')

  const onSubmit = async (data: any) => {
    if (!imageUrl) {
      toast({
        title: "Error",
        description: "Please upload an image",
        variant: "destructive",
      })
      return
    }

    setLoading(true)
    
    try {
      const contentImageData = {
        section_name: data.section_name,
        image_url: imageUrl,
        alt_text: data.alt_text || null,
        display_order: parseInt(data.display_order),
        is_active: data.is_active,
      }

      if (contentImage) {
        const { error } = await supabase
          .from('content_images')
          .update(contentImageData)
          .eq('id', contentImage.id)
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Content image updated successfully",
        })
      } else {
        const { error } = await supabase
          .from('content_images')
          .insert([contentImageData])
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Content image created successfully",
        })
      }
      
      onSave()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save content image",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{contentImage ? 'Edit' : 'Add'} Banner Image</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="section_name">Section</Label>
            <Select 
              value={watchedSectionName} 
              onValueChange={(value) => setValue('section_name', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a section" />
              </SelectTrigger>
              <SelectContent>
                {SECTION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.section_name && (
              <p className="text-sm text-red-600">Section is required</p>
            )}
          </div>

          <ImageUpload
            currentImageUrl={imageUrl}
            onImageUpload={setImageUrl}
            onImageRemove={() => setImageUrl('')}
            label="Section Image"
            folder="content"
          />

          <div className="space-y-2">
            <Label htmlFor="alt_text">Alt Text</Label>
            <Input
              id="alt_text"
              {...register('alt_text')}
              placeholder="Alternative text for accessibility"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              {...register('display_order', { valueAsNumber: true })}
              placeholder="0"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={watchedIsActive}
              onCheckedChange={(checked) => setValue('is_active', checked)}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ContentImageForm
